import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, limit, doc, deleteDoc, startAfter, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { isAdmin } from '../config/accessControl';

const PAGE_SIZE = 50;

function Conversations({ selectedCompany, apiBaseUrl, apiKey, userEmail }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (userEmail) {
        setIsUserAdmin(await isAdmin(userEmail));
      } else {
        setIsUserAdmin(false);
      }
    };
    checkAdminStatus();
  }, [userEmail]);

  useEffect(() => {
    if (selectedCompany) {
      fetchConversations(true);
    } else {
      setConversations([]);
      setSelectedConversation(null);
      setTotalCount(0);
      setHasMore(true);
      setLastVisible(null);
    }
    setSelectedConversation(null);
  }, [selectedCompany, apiBaseUrl, apiKey]);

  const fetchConversations = async (isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const companyLower = selectedCompany.toLowerCase();
      const usePostgres = companyLower === 'smartmed_2' || companyLower === 'uki_clinic_almaty';

      if (usePostgres) {
        if (!apiBaseUrl || !apiKey) {
          setError('API configuration is missing. Cannot fetch data.');
          setLoading(false);
          return;
        }

        const offset = isInitial ? 0 : conversations.length;
        // Clean endpoint paths: /smartmed_2/ or /uki_clinic_almaty/
        const response = await fetch(`${apiBaseUrl}/${selectedCompany}/conversations?limit=${PAGE_SIZE}&offset=${offset}`, {
          headers: { 'X-Access-Token': apiKey }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const filteredData = data.filter(conversation => {
          const callerId = conversation.caller_id || '';
          return !callerId.includes('FollowupAnalyzer') &&
                 !callerId.includes('WarmupGenerator') &&
                 !callerId.includes('TopicDecision');
        });

        if (isInitial) {
          setConversations(filteredData);
          if (filteredData.length > 0) setSelectedConversation(filteredData[0]);
          else setSelectedConversation(null);

          // Fetch total count
          const countResponse = await fetch(`${apiBaseUrl}/${selectedCompany}/conversations/count`, {
            headers: { 'X-Access-Token': apiKey }
          });
          if (countResponse.ok) {
            const countData = await countResponse.json();
            setTotalCount(countData.count || 0);
          }
        } else {
          setConversations(prev => [...prev, ...filteredData]);
        }

        setHasMore(filteredData.length === PAGE_SIZE);

      } else {
        const conversationsRef = collection(db, 'production', selectedCompany, 'conversations');
        let q;

        if (isInitial) {
          q = query(conversationsRef, orderBy('last_updated', 'desc'), limit(PAGE_SIZE));

          // Get total count
          const countSnapshot = await getCountFromServer(conversationsRef);
          setTotalCount(countSnapshot.data().count);
        } else {
          q = query(conversationsRef, orderBy('last_updated', 'desc'), startAfter(lastVisible), limit(PAGE_SIZE));
        }

        const querySnapshot = await getDocs(q);
        const conversationsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const filteredConversations = conversationsList.filter(conversation => {
          const callerId = conversation.caller_id || '';
          return !callerId.includes('FollowupAnalyzer') &&
                 !callerId.includes('WarmupGenerator') &&
                 !callerId.includes('TopicDecision');
        });

        if (isInitial) {
          setConversations(filteredConversations);
          if (filteredConversations.length > 0) setSelectedConversation(filteredConversations[0]);
          else setSelectedConversation(null);
        } else {
          setConversations(prev => [...prev, ...filteredConversations]);
        }

        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length === PAGE_SIZE);
      }

    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError(`Failed to load conversations: ${error.message}`);
    } finally {
      if (isInitial) setLoading(false);
      else setLoadingMore(false);
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
  };

  const searchConversations = async (query) => {
    if (!query.trim()) {
      clearSearch();
      return;
    }

    const companyLower = selectedCompany.toLowerCase();
    const usePostgres = companyLower === 'smartmed_2' || companyLower === 'uki_clinic_almaty';

    if (!usePostgres) {
      setError('Search is only available for SmartMed and UKI Clinic');
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiBaseUrl}/${selectedCompany}/conversations/search?phone=${encodeURIComponent(query)}`,
        { headers: { 'X-Access-Token': apiKey } }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const filteredData = data.filter(conversation => {
        const callerId = conversation.caller_id || '';
        return !callerId.includes('FollowupAnalyzer') &&
               !callerId.includes('WarmupGenerator') &&
               !callerId.includes('TopicDecision');
      });

      setConversations(filteredData);
      setIsSearchMode(true);
      setHasMore(false);
      if (filteredData.length > 0) {
        setSelectedConversation(filteredData[0]);
      } else {
        setSelectedConversation(null);
      }
    } catch (err) {
      console.error('Error searching conversations:', err);
      setError(`Search failed: ${err.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearchMode(false);
    fetchConversations(true);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchConversations(searchQuery);
    }
  };

  const handleDeleteConversation = async (conversationId, companyName) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }
    setDeletingId(conversationId);
    setError(null);

    try {
      const companyLower = companyName.toLowerCase();
      const usePostgres = companyLower === 'smartmed_2' || companyLower === 'uki_clinic_almaty';

      if (usePostgres) {
        if (!apiBaseUrl || !apiKey) throw new Error('API configuration missing for delete.');
        // Clean endpoint paths: /smartmed_2/ or /uki_clinic_almaty/
        const response = await fetch(`${apiBaseUrl}/${companyName}/conversations/${conversationId}`, {
          method: 'DELETE',
          headers: { 'X-Access-Token': apiKey }
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `Failed to delete conversation: ${response.status}`);
        }
      } else { // Firebase based deletion
        await deleteDoc(doc(db, 'production', companyName, 'conversations', conversationId));
      }

      setConversations(prevConvos => prevConvos.filter(convo => convo.id !== conversationId));
      if (selectedConversation?.id === conversationId) {
        const newConversations = conversations.filter(convo => convo.id !== conversationId);
        setSelectedConversation(newConversations.length > 0 ? newConversations[0] : null);
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError(`Failed to delete conversation: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestampInput) => {
    if (!timestampInput) return 'N/A';
    try {
      let date;
      if (timestampInput && typeof timestampInput.toDate === 'function') { // Firestore Timestamp
        date = timestampInput.toDate();
      } else if (typeof timestampInput === 'string') { // ISO Date String or other string date
        date = new Date(timestampInput);
        if (isNaN(date.getTime())) {
          // If string parsing fails, return the original string, it might be pre-formatted.
          return timestampInput;
        }
      } else if (timestampInput instanceof Date) { // Already a JS Date object
          date = timestampInput;
      } else {
        // For any other type, return as string
        return String(timestampInput);
      }

      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: true
      }).format(date);
    } catch (error) {
      // console.error('Error formatting timestamp in Conversations.js:', error, timestampInput);
      return String(timestampInput); // Fallback to original string representation
    }
  };

  if (!selectedCompany) {
    return <div className="p-4">Please select a company to view conversations.</div>;
  }

  return (
    <div className="flex flex-col md:flex-row gap-4">
      {/* Left panel - List of conversations */}
      <div className="w-full md:w-1/3 bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Conversations</h3>
          <p className="text-sm text-gray-500">
            {isSearchMode
              ? `${conversations.length} result${conversations.length !== 1 ? 's' : ''} found`
              : (
                <>
                  {totalCount > 0 && `${totalCount} total`}
                  {totalCount > 0 && conversations.length > 0 && conversations.length < totalCount && ` (${conversations.length} loaded)`}
                  {totalCount === 0 && conversations.length === 0 && !loading && 'No conversations'}
                  {totalCount === 0 && conversations.length > 0 && `${conversations.length} conversations`}
                </>
              )
            }
          </p>

          {/* Search input - only for PostgreSQL companies */}
          {(selectedCompany.toLowerCase() === 'smartmed_2' || selectedCompany.toLowerCase() === 'uki_clinic_almaty') && (
            <div className="mt-3 flex gap-2">
              <input
                type="text"
                placeholder="Search by phone number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={() => searchConversations(searchQuery)}
                disabled={isSearching || !searchQuery.trim()}
                className="px-3 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching ? '...' : 'Search'}
              </button>
              {isSearchMode && (
                <button
                  onClick={clearSearch}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {loading && <div className="p-4 text-center">Loading conversations...</div>}

        {error && (
          <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded mx-4 my-2">
            {error}
            <button
              onClick={() => fetchConversations(true)}
              className="ml-4 px-2 py-1 bg-red-200 rounded hover:bg-red-300"
            >
              Retry
            </button>
          </div>
        )}

        <div className="overflow-y-auto max-h-[60vh]">
          {conversations.map((conversation) => (
            <div
              key={conversation.id}
              className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${selectedConversation?.id === conversation.id ? 'bg-blue-50' : ''}`}
              onClick={() => handleSelectConversation(conversation)}
            >
              <div className="flex justify-between">
                <span className="font-medium text-gray-900 truncate">
                  {conversation.caller_id || 'Unknown caller'}
                </span>
                <span className="text-xs text-gray-500">
                  {formatTimestamp(conversation.last_updated)}
                </span>
              </div>
              <div className="text-sm text-gray-500 truncate mt-1">
                {conversation.messages && conversation.messages.length > 0
                  ? conversation.messages[conversation.messages.length - 1]?.content || 'No message content'
                  : 'No messages'}
              </div>
            </div>
          ))}

          {conversations.length === 0 && !loading && !error && (
            <div className="p-4 text-center text-gray-500">
              No conversations found for this company.
            </div>
          )}

          {hasMore && conversations.length > 0 && (
            <div className="p-4 text-center border-t">
              <button
                onClick={() => fetchConversations(false)}
                disabled={loadingMore}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? 'Loading...' : 'Show more'}
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Right panel - Conversation details */}
      <div className="w-full md:w-2/3 bg-white rounded-lg shadow">
        {selectedConversation ? (
          <div>
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  Conversation Details
                </h3>
                {isUserAdmin && (
                  <button
                    onClick={() => handleDeleteConversation(selectedConversation.id, selectedCompany)}
                    disabled={deletingId === selectedConversation.id}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deletingId === selectedConversation.id ? 'Deleting...' : 'Delete Conversation'}
                  </button>
                )}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Caller:</span>{' '}
                  <span className="font-medium">{selectedConversation.caller_id || 'Unknown'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Last Updated:</span>{' '}
                  <span className="font-medium">{formatTimestamp(selectedConversation.last_updated)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Call SID:</span>{' '}
                  <span className="font-medium">{selectedConversation.last_call_sid || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Call Ended:</span>{' '}
                  <span className="font-medium">{selectedConversation.call_ended ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <h4 className="text-md font-medium text-gray-900 mb-4">Messages</h4>
              
              <div className="space-y-4 max-h-[60vh] overflow-y-auto p-2">
                {selectedConversation.messages && selectedConversation.messages.length > 0 ? (
                  selectedConversation.messages.map((message, index) => (
                    <div 
                      key={index}
                      className={`p-3 rounded-lg max-w-[80%] ${
                        (() => {
                          // Enhanced styling based on source
                          if (message.source) {
                            switch (message.source) {
                              case 'user':
                                return 'bg-blue-100 ml-auto';
                              case 'human_operator':
                                return 'bg-green-100';
                              case 'ai':
                                return 'bg-gray-100';
                              default:
                                return message.role === 'user' ? 'bg-blue-100 ml-auto' : 'bg-gray-100';
                            }
                          } else {
                            // Fallback to role-based styling
                            return message.role === 'user' ? 'bg-blue-100 ml-auto' : 'bg-gray-100';
                          }
                        })()
                      }`}
                    >
                      <div className="font-medium text-xs text-gray-500 mb-1">
                        {(() => {
                          // Check the source column first if available
                          if (message.source) {
                            switch (message.source) {
                              case 'ai':
                                return '🤖 Assistant';
                              case 'human_operator':
                                return message.operator_name ? `👤 ${message.operator_name}` : '👤 Human Operator';
                              case 'operator_handback':
                                return message.operator_name ? `👤 ${message.operator_name}` : '👤 Human Operator';
                              case 'user':
                                return 'Пользователь';
                              default:
                                return message.role === 'user' ? 'Пользователь' : '🤖 Assistant';
                            }
                          } else {
                            // Fallback to role-based logic if source is not available
                            return message.role === 'user' ? 'Пользователь' : '🤖 Assistant';
                          }
                        })()} 
                        {message.timestamp && ` • ${formatTimestamp(message.timestamp)}`}
                      </div>
                      <div className="text-gray-800 whitespace-pre-line">
                        {message.content}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500">No messages in this conversation.</div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            Select a conversation to view details.
          </div>
        )}
      </div>
    </div>
  );
}

export default Conversations; 